import { createApi } from '@reduxjs/toolkit/query/react';
import { publicBaseQuery } from '@core/api';

/**
 * Location Availability API using RTK Query
 *
 * PUBLIC API - No authentication required
 * Provides endpoints for candidates to update their geographic location availability via WhatsApp campaigns
 */
export const locationAvailabilityApi = createApi({
  reducerPath: 'locationAvailabilityApi',
  baseQuery: publicBaseQuery,
  tagTypes: ['LocationAvailability'],
  endpoints: (builder) => ({
    /**
     * Get campaign response details
     * @param {string} id - Campaign response UUID
     * @returns Campaign details including candidate name, campaign type, etc.
     */
    getCampaignResponse: builder.query({
      query: (id) => ({
        url: `/api/v1/campaigns/${id}/`,
        // Add cache buster to prevent browser caching
        params: { _: Date.now() },
      }),
      // Don't cache this endpoint (always fetch fresh)
      keepUnusedDataFor: 0,
      providesTags: (result, error, id) => [{ type: 'LocationAvailability', id }],
    }),

    /**
     * Submit location availability response
     * @param {Object} params
     * @param {string} params.id - Campaign response UUID
     * @param {string} params.answer - "yes" or "no"
     * @param {Object} params.response_data - Response data object with location fields (required for answer=yes)
     * @returns Success message with updated campaign response
     */
    submitCampaignResponse: builder.mutation({
      query: ({ id, answer, response_data }) => ({
        url: `/api/v1/campaigns/${id}/submit/`,
        method: 'POST',
        body: {
          answer,
          response_data,
        },
      }),
      // Invalidate the campaign details after submission
      invalidatesTags: (result, error, { id }) => [{ type: 'LocationAvailability', id }],
    }),
  }),
});

export const { useGetCampaignResponseQuery, useSubmitCampaignResponseMutation } = locationAvailabilityApi;
